import { useCallback, useEffect, useRef, useState } from "react";

type UseInfiniteScrollOptions = {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
};

export function useInfiniteScroll({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = "100px",
  threshold = 0.1,
  enabled = true
}: UseInfiniteScrollOptions) {
  const [isMounted, setIsMounted] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const loadingStateRef = useRef(false);

  // Use refs to store current values to avoid stale closures
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  const onLoadMoreRef = useRef(onLoadMore);
  const enabledRef = useRef(enabled);

  // Update refs when values change
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    loadingStateRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (
        entry?.isIntersecting &&
        hasMoreRef.current &&
        !loadingStateRef.current &&
        isMounted &&
        enabledRef.current
      ) {
        // Set loading state immediately to prevent duplicate calls
        loadingStateRef.current = true;
        onLoadMoreRef.current();
      }
    },
    [isMounted]
  );

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Setup observer - only recreate when absolutely necessary
  useEffect(() => {
    if (!isMounted || !enabled) return undefined;

    // Small delay to ensure DOM is ready after filter/sort changes
    const setupObserver = () => {
      const currentTarget = targetRef.current;
      if (!currentTarget) return;

      // Disconnect existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new observer
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin,
        threshold
      });

      observerRef.current.observe(currentTarget);
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      setupObserver();
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, rootMargin, threshold, isMounted, enabled]);

  // Force re-observe when hasMore changes from false to true (after reset)
  useEffect(() => {
    if (hasMore && !isLoading && observerRef.current && targetRef.current && enabled) {
      // Re-observe the target to trigger immediate check
      observerRef.current.unobserve(targetRef.current);
      observerRef.current.observe(targetRef.current);
    }
  }, [hasMore, isLoading, enabled]);

  return { targetRef };
}
